import type { ReactNode } from 'react';
import type { Placement } from '@floating-ui/react';
import type { DotPosition, OffsetStyle } from '../types';
import { OnboardingHotspot } from './onboarding-hotspot';
import { OnboardingInfoTooltip } from './onboarding-info-tooltip';

interface OnboardingHotspotWithTooltipProps {
  /**
   * If separate hotspotTarget / tooltipTarget are not provided,
   * this target is used for both the guided hotspot and info tooltip.
   */
  target?: string;

  /**
   * Shared selector for both hotspot and tooltip.
   *
   * Use this when the same nested DOM element should be used as the
   * visual/click anchor for both the hotspot and the info tooltip.
   */
  targetSelector?: string;
  hotspotTarget?: string;
  hotspotTargetSelector?: string;
  tooltipTarget?: string;
  tooltipTargetSelector?: string;
  enabled?: boolean;
  children: ReactNode;
  placement?: Placement;
  tooltipOffset?: number;
  position?: DotPosition;
  hotspotOffset?: OffsetStyle;
}

export function OnboardingHotspotWithTooltip({
  target,
  targetSelector,
  hotspotTarget,
  hotspotTargetSelector,
  tooltipTarget,
  tooltipTargetSelector,
  enabled = true,
  children,
  placement,
  tooltipOffset,
  position,
  hotspotOffset,
}: OnboardingHotspotWithTooltipProps) {
  if (!enabled) {
    return <>{children}</>;
  }

  const resolvedHotspotTarget = hotspotTarget ?? target;
  const resolvedTooltipTarget = tooltipTarget ?? target;

  const resolvedHotspotTargetSelector = hotspotTargetSelector ?? targetSelector;
  const resolvedTooltipTargetSelector = tooltipTargetSelector ?? targetSelector;

  /**
   * If no target is provided, render children untouched.
   * This keeps the component safe for conditional usage.
   */
  if (!resolvedHotspotTarget && !resolvedTooltipTarget) {
    return <>{children}</>;
  }

  const content = resolvedHotspotTarget ? (
    <OnboardingHotspot
      target={resolvedHotspotTarget}
      targetSelector={resolvedHotspotTargetSelector}
      position={position}
      offset={hotspotOffset}
    >
      {children}
    </OnboardingHotspot>
  ) : (
    children
  );

  if (!resolvedTooltipTarget) {
    return <>{content}</>;
  }

  return (
    <OnboardingInfoTooltip
      target={resolvedTooltipTarget}
      targetSelector={resolvedTooltipTargetSelector}
      placement={placement}
      offset={tooltipOffset}
    >
      {content}
    </OnboardingInfoTooltip>
  );
}
